import React, { useEffect, useState } from 'react';
import styles from './AdminShared.module.scss';
import { VideoParticipantTypeCreateDto, VideoParticipantTypeDto } from '../../../types/admin.types';
import {
    createVideoParticipantType,
    deleteVideoParticipantType,
    fetchVideoParticipantTypes,
    updateVideoParticipantType
} from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';

const emptyForm: VideoParticipantTypeCreateDto = {
    Name: '',
    Description: ''
};

const AdminVideoParticipantTypes = () => {
    const { getUserInfo } = useUserState();
    const [items, setItems] = useState<VideoParticipantTypeDto[]>([]);
    const [form, setForm] = useState<VideoParticipantTypeCreateDto>(emptyForm);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const token = getUserInfo()?.token;

    const loadItems = async () => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const response = await fetchVideoParticipantTypes(token);
            setItems(response);
        } catch (error) {
            setErrorMessage('Unable to load participant types.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadItems();
    }, []);

    const resetForm = () => {
        setForm(emptyForm);
        setSelectedId(null);
    };

    const handleEdit = (item: VideoParticipantTypeDto) => {
        setSelectedId(item.Id);
        setForm({
            Name: item.Name,
            Description: item.Description ?? ''
        });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage(null);
        try {
            if (selectedId) {
                await updateVideoParticipantType({ ...form, Id: selectedId }, token);
            } else {
                await createVideoParticipantType(form, token);
            }
            await loadItems();
            resetForm();
        } catch (error) {
            setErrorMessage('Unable to save participant type.');
        }
    };

    const handleDelete = async (id: string) => {
        setErrorMessage(null);
        try {
            await deleteVideoParticipantType(id, token);
            if (selectedId === id) {
                resetForm();
            }
            await loadItems();
        } catch (error) {
            setErrorMessage('Unable to delete participant type.');
        }
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Video Participant Types</h1>
                    <p>Manage participant roles such as fighter, interviewer, or coach.</p>
                </div>
            </header>

            {errorMessage ? <div className={styles.muted}>{errorMessage}</div> : null}

            <div className={styles.content}>
                <section className={styles.panel}>
                    <h2>Participant Types</h2>
                    {isLoading ? (
                        <div className={styles.muted}>Loading types...</div>
                    ) : items.length === 0 ? (
                        <div className={styles.empty}>No participant types yet.</div>
                    ) : (
                        items.map((item) => (
                            <div key={item.Id} className={styles.listItem}>
                                <div>
                                    <strong>{item.Name}</strong>
                                    <div className={styles.muted}>{item.Description}</div>
                                </div>
                                <div className={styles.rowActions}>
                                    <button type="button" className={styles.secondary} onClick={() => handleEdit(item)}>Edit</button>
                                    <button type="button" className={styles.danger} onClick={() => handleDelete(item.Id)}>Delete</button>
                                </div>
                            </div>
                        ))
                    )}
                </section>

                <section className={styles.panel}>
                    <h2>{selectedId ? 'Edit Participant Type' : 'New Participant Type'}</h2>
                    <form onSubmit={handleSubmit}>
                        <div className={styles.field}>
                            <label htmlFor="participant-type-name">Name</label>
                            <input
                                id="participant-type-name"
                                value={form.Name}
                                onChange={(event) => setForm({ ...form, Name: event.target.value })}
                                maxLength={200}
                                required
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor="participant-type-description">Description</label>
                            <textarea
                                id="participant-type-description"
                                value={form.Description}
                                onChange={(event) => setForm({ ...form, Description: event.target.value })}
                                maxLength={2000}
                            />
                        </div>
                        <div className={styles.actions}>
                            <button className={styles.primary} type="submit">
                                {selectedId ? 'Update Type' : 'Create Type'}
                            </button>
                            {selectedId ? (
                                <button type="button" className={styles.secondary} onClick={resetForm}>Cancel</button>
                            ) : null}
                        </div>
                    </form>
                </section>
            </div>
        </div>
    );
};

export default AdminVideoParticipantTypes;
